import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSuspenseCurrentUser } from 'FrontRoyalAngular';
import { angularInjectorProvider } from 'Injector';

/**
 * Determines the home path based on user authentication state and platform.
 * Logic mirrors Angular's $rootScope.homePath:
 * - CORDOVA without user: /onboarding/hybrid
 * - No user: /
 * - User exists: /dashboard
 */
function getHomePath(): string {
    const $rootScope = angularInjectorProvider.get('$rootScope') as {
        currentUser?: unknown;
    };
    const $window = angularInjectorProvider.get('$window') as { CORDOVA?: boolean };

    if ($window.CORDOVA && !$rootScope.currentUser) {
        return '/onboarding/hybrid';
    }

    if (!$rootScope.currentUser) {
        return '/';
    }

    return '/dashboard';
}

/**
 * Redirects to the user's home path (/home route).
 * Equivalent to Angular's redirectToHome resolver.
 */
export function HomeRedirect() {
    const [redirectPath, setRedirectPath] = useState<string | null>(null);
    const currentUser = useSuspenseCurrentUser();

    useEffect(() => {
        setRedirectPath(getHomePath());
    }, [currentUser]);

    if (!redirectPath) {
        return null; // Loading
    }

    return <Navigate to={redirectPath} replace />;
}

/**
 * Handles root path (/) redirect.
 * Equivalent to Angular's redirectToRootDefault resolver.
 */
export function RootRedirect() {
    useEffect(() => {
        if (window.CORDOVA) {
            // In CORDOVA, use ValidationResponder to handle last_visited_route
            const ValidationResponder = angularInjectorProvider.get('ValidationResponder') as {
                forwardToTargetUrl: () => void;
            };
            ValidationResponder.forwardToTargetUrl();
        } else {
            window.location.href = '/';
        }
    }, []);

    // Return null since we're handling navigation imperatively
    return null;
}
